import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FolderKanban, Image as ImageIcon, MessageSquare, Pin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { classNames } from '../../utils/classNames';
import { useI18n } from '../../hooks/useI18n';
import {
  deleteRecentActivityApi,
  fetchRecentActivityApi,
  updateRecentActivityApi
} from '../../api/activity.api';
import { RecentItemMenu } from './RecentItemMenu';
import { groupRecentByDate } from './groupRecentByDate';
import { notifyRecentActivityRemoved, subscribeRecentActivity } from './recentActivityEvents';
import { getCachedRecentItems, setCachedRecentItems } from './sidebarListsCache';

const recentIcon = (type) => {
  const iconClass = 'h-4 w-4 shrink-0 opacity-70';
  if (type === 'conversation') return <MessageSquare className={iconClass} />;
  if (type === 'image') return <ImageIcon className={iconClass} />;
  return <FolderKanban className={iconClass} />;
};

const recentPath = (item) => {
  if (item.type === 'conversation') return `/chat/${item.id}`;
  if (item.type === 'image') return `/images/${item.id}`;
  return `/workspace/${item.id}`;
};

const GROUP_LABEL_KEYS = {
  recent: 'recentToday',
  yesterday: 'recentYesterday',
  lastWeek: 'recentLastWeek',
  last30Days: 'recentLast30Days',
  older: 'recentOlder'
};

export const RecentActivityList = ({
  take = 100,
  fallbackItems = [],
  compact = false,
  onNavigate,
  folders = [],
  className = '',
  excludeFoldered = true,
  folderIdFilter = null,
  onCreateFolder,
  groupByDate = false,
  types = null
}) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [items, setItems] = useState(() => getCachedRecentItems());
  const [loading, setLoading] = useState(() => getCachedRecentItems().length === 0);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  const commitItems = useCallback((next, options) => {
    setItems((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      return setCachedRecentItems(resolved, options);
    });
  }, []);

  const reload = useCallback(async () => {
    try {
      const next = await fetchRecentActivityApi(take);
      commitItems(next, { take, fromFetch: true });
      setError('');
    } catch (err) {
      if (getCachedRecentItems().length === 0) {
        setError(err.message || t('recentLoadError'));
      }
    } finally {
      setLoading(false);
    }
  }, [commitItems, t, take]);

  useEffect(() => {
    let cancelled = false;
    if (getCachedRecentItems().length === 0) {
      setLoading(true);
    }

    fetchRecentActivityApi(take)
      .then((next) => {
        if (!cancelled) {
          commitItems(next, { take, fromFetch: true });
          setError('');
        }
      })
      .catch(() => {
        if (!cancelled && getCachedRecentItems().length === 0) {
          commitItems([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [commitItems, take]);

  useEffect(() => {
    const upsertItem = (item) => {
      if (!item?.id || !item?.type) return;
      commitItems((prev) => {
        const index = prev.findIndex((entry) => entry.id === item.id && entry.type === item.type);
        if (index >= 0) {
          const next = [...prev];
          const current = next[index];
          next[index] = {
            ...current,
            ...item,
            folderId: item.folderId === undefined ? current.folderId : item.folderId
          };
          return next;
        }
        return [
          {
            isPinned: false,
            isArchived: false,
            folderId: null,
            isTemporary: false,
            updatedAt: new Date().toISOString(),
            title: '',
            ...item
          },
          ...prev
        ];
      });
      setLoading(false);
      setError('');
    };

    return subscribeRecentActivity({
      onUpsert: upsertItem,
      onReload: () => {
        reload();
      }
    });
  }, [commitItems, reload]);

  const displayItems = useMemo(() => {
    const source = items.length > 0 ? items : fallbackItems;
    return [...source]
      .filter((item) => {
        if (types?.length && !types.includes(item.type)) return false;
        if (folderIdFilter) return item.folderId === folderIdFilter;
        if (excludeFoldered) return !item.folderId;
        return true;
      })
      .sort((a, b) => {
        const pinDiff = Number(Boolean(b.isPinned)) - Number(Boolean(a.isPinned));
        if (pinDiff !== 0) return pinDiff;
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      });
  }, [excludeFoldered, fallbackItems, folderIdFilter, items, types]);

  const groups = useMemo(() => groupRecentByDate(displayItems), [displayItems]);

  const go = (item) => {
    const path = recentPath(item);
    onNavigate?.(path);
    navigate(path);
  };

  const patchItem = (updated) => {
    commitItems((prev) => {
      const next = prev.map((entry) =>
        entry.id === updated.id && entry.type === updated.type ? { ...entry, ...updated } : entry
      );
      return next.filter((entry) => !entry.isArchived);
    });
  };

  const handleRename = async (item, title) => {
    setBusyId(item.id);
    try {
      const updated = await updateRecentActivityApi(item.type, item.id, { title });
      patchItem(updated);
    } finally {
      setBusyId(null);
    }
  };

  const handleTogglePin = async (item) => {
    setBusyId(item.id);
    try {
      const updated = await updateRecentActivityApi(item.type, item.id, { isPinned: !item.isPinned });
      patchItem(updated);
      await reload();
    } finally {
      setBusyId(null);
    }
  };

  const handleArchive = async (item) => {
    setBusyId(item.id);
    try {
      await updateRecentActivityApi(item.type, item.id, { isArchived: true });
      commitItems((prev) => prev.filter((entry) => !(entry.id === item.id && entry.type === item.type)));
      notifyRecentActivityRemoved(item);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (item) => {
    setBusyId(item.id);
    try {
      await deleteRecentActivityApi(item.type, item.id);
      commitItems((prev) => prev.filter((entry) => !(entry.id === item.id && entry.type === item.type)));
      notifyRecentActivityRemoved(item);
    } finally {
      setBusyId(null);
    }
  };

  const handleMoveToFolder = async (item, folderId) => {
    if (folderId === '__new__') {
      onCreateFolder?.(item);
      return;
    }
    setBusyId(item.id);
    try {
      const updated = await updateRecentActivityApi(item.type, item.id, {
        folderId: folderId || undefined,
        clearFolder: !folderId
      });
      patchItem(updated);
      await reload();
    } finally {
      setBusyId(null);
    }
  };

  const renderItem = (item) => (
    <div
      key={`${item.type}-${item.id}`}
      className={classNames(
        'group relative flex items-center gap-0.5 rounded-lg transition hover:bg-[#F5F5F5] dark:hover:bg-neutral-900',
        compact ? 'px-1.5 py-1' : 'px-1.5 py-1'
      )}
    >
      <button
        type="button"
        onClick={() => go(item)}
        className="flex min-w-0 flex-1 items-center gap-2 truncate text-start text-sm leading-5 text-neutral-600 hover:text-neutral-950 dark:text-neutral-400 dark:hover:text-white"
      >
        <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center">
          {item.isPinned ? <Pin className="h-3.5 w-3.5 shrink-0 text-neutral-500" /> : recentIcon(item.type)}
        </span>
        <span className="truncate">{item.title || item.name}</span>
        {item.isTemporary ? (
          <span className="shrink-0 rounded bg-amber-100 px-1 py-px text-[10px] text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
            {t('chatTemporary')}
          </span>
        ) : null}
      </button>
      <RecentItemMenu
        item={item}
        folders={folders}
        disabled={busyId === item.id}
        onRename={(title) => handleRename(item, title)}
        onTogglePin={() => handleTogglePin(item)}
        onArchive={() => handleArchive(item)}
        onDelete={() => handleDelete(item)}
        onMoveToFolder={(folderId) => handleMoveToFolder(item, folderId)}
      />
    </div>
  );

  if (loading && displayItems.length === 0) {
    return <div className="px-2 py-4 text-xs text-neutral-400">{t('loading')}</div>;
  }

  if (displayItems.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-neutral-200 px-3 py-6 text-center text-xs text-neutral-400 dark:border-neutral-800">
        {t('sidebarNoRecent')}
      </div>
    );
  }

  return (
    <div className={classNames('flex flex-col gap-0', className)}>
      {error ? <p className="px-2 pb-1 text-xs text-red-500">{error}</p> : null}
      {groupByDate
        ? groups.map((group) => (
            <div key={group.key} className="flex flex-col gap-0">
              {group.key !== 'recent' ? (
                <div className="px-1.5 pb-0.5 pt-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-400">
                  {t(GROUP_LABEL_KEYS[group.key] || 'recentToday')}
                </div>
              ) : null}
              {group.items.map(renderItem)}
            </div>
          ))
        : displayItems.map(renderItem)}
    </div>
  );
};
